import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Check, LogOut, X } from 'lucide-react'
import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { acceptInvitation, invitationEntryQueryOptions } from '@/features/invitations/api'
import { InvitationCard } from '@/features/invitations/invitation-card'
import { InviteShell } from '@/features/invitations/invite-shell'
import { ApiError } from '@/lib/api'
import { authClient } from '@/lib/auth'
import { refreshSession, sessionQueryOptions } from '@/lib/session'
import { refreshWorkspaces } from '@/lib/workspace'

export const Route = createFileRoute('/invite/$invitationId')({
  component: InviteRoute,
})

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return error.problem.detail ?? error.problem.title
  }

  if (error instanceof Error) {
    return error.message
  }

  return fallback
}

function normalizeEmail(email: string) {
  return email.trim().toLowerCase()
}

function InviteRoute() {
  const { invitationId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const entryQuery = useQuery(invitationEntryQueryOptions(invitationId))
  const sessionQuery = useQuery(sessionQueryOptions)
  const session = sessionQuery.data
  const entry = entryQuery.data
  const redirectPath = `/invite/${invitationId}`

  useEffect(() => {
    if (!entry || sessionQuery.isPending || session) {
      return
    }

    const search = { redirect: redirectPath, email: entry.email }

    void navigate(
      entry.action === 'signup'
        ? { to: '/signup', search, replace: true }
        : { to: '/login', search, replace: true },
    )
  }, [entry, navigate, redirectPath, session, sessionQuery.isPending])

  const acceptMutation = useMutation({
    mutationFn: (email: string) => acceptInvitation(invitationId, email),
    onSuccess: async (response) => {
      await refreshSession(queryClient)
      await refreshWorkspaces(queryClient)
      await navigate({
        to: '/w/$workspaceSlug',
        params: { workspaceSlug: response.workspace.slug },
      })
    },
  })

  async function handleSignOut() {
    await authClient.signOut()
    await refreshSession(queryClient)
  }

  if (entryQuery.isLoading || sessionQuery.isPending) {
    return (
      <InviteShell content={<div className="h-48 animate-pulse rounded-lg border bg-card" />} />
    )
  }

  if (entryQuery.error || !entry) {
    return (
      <InviteShell
        content={
          <div className="rounded-lg border bg-card p-6 shadow-sm">
            <span className="flex size-12 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
              <X className="size-6" />
            </span>
            <h1 className="mt-5 font-heading text-2xl text-foreground">Pozvánka není dostupná</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {getErrorMessage(entryQuery.error, 'Odkaz je neplatný nebo vypršel.')}
            </p>
          </div>
        }
      />
    )
  }

  if (!session) {
    return (
      <InviteShell
        content={
          <InvitationCard entry={entry}>
            <p className="mt-5 text-sm text-muted-foreground">
              Přesměrovávám na {entry.action === 'signup' ? 'registraci' : 'přihlášení'}.
            </p>
          </InvitationCard>
        }
      />
    )
  }

  const signedInEmail = session.user.email
  const emailMatches = normalizeEmail(signedInEmail) === normalizeEmail(entry.email)

  if (!emailMatches) {
    return (
      <InviteShell
        content={
          <InvitationCard entry={entry}>
            <p
              role="alert"
              className="mt-5 rounded-lg bg-destructive/10 p-3 text-sm text-destructive"
            >
              Jste přihlášen(a) jako {signedInEmail}, ale pozvánka je pro {entry.email}.
            </p>
            <Button className="mt-6 w-full" variant="outline" onClick={handleSignOut}>
              <LogOut className="size-4" />
              Odhlásit se a pokračovat
            </Button>
          </InvitationCard>
        }
      />
    )
  }

  return (
    <InviteShell
      content={
        <InvitationCard entry={entry}>
          {acceptMutation.error && (
            <p
              role="alert"
              className="mt-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive"
            >
              {getErrorMessage(acceptMutation.error, 'Pozvánku se nepodařilo přijmout.')}
            </p>
          )}

          <Button
            className="mt-6 w-full"
            disabled={acceptMutation.isPending}
            onClick={() => acceptMutation.mutate(entry.email)}
          >
            <Check className="size-4" />
            {acceptMutation.isPending ? 'Přijímám…' : 'Přijmout pozvánku'}
          </Button>
        </InvitationCard>
      }
    />
  )
}
